import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Compass, Mountain, ClipboardCheck, Scale, Lock, Award, Quote, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRINCIPIO_ICONS = [ClipboardCheck, Scale, Lock, Award];

export const EMPTY_FILOSOFIA = {
    mision: "",
    vision: "",
    principios: [{ nombre: "", frase: "" }, { nombre: "", frase: "" }, { nombre: "", frase: "" }, { nombre: "", frase: "" }],
    cita: "",
    cita_autor: "",
};

const reveal = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

function Tarjeta({ icon: Icon, titulo, texto, delay, testid }) {
    return (
        <motion.article
            {...reveal(delay)}
            className="rounded-lg border border-navy/10 bg-white p-8 shadow-[0_18px_40px_-24px_rgba(10,17,40,0.35)] md:p-12"
            data-testid={testid}
        >
            <span className="mb-8 flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 text-gold-dim" aria-hidden="true">
                <Icon size={26} strokeWidth={1.5} />
            </span>
            <h3 className="mb-6 text-sm font-semibold uppercase tracking-[0.35em] text-navy">{titulo}</h3>
            <p className="font-display text-base leading-[1.75] text-navy/85 md:text-lg">{texto}</p>
        </motion.article>
    );
}

export default function Filosofia({ content, preview = false }) {
    const [data, setData] = useState(content || null);

    useEffect(() => {
        if (content) {
            setData(content);
            return;
        }
        axios.get(`${API}/site-content/filosofia`).then((r) => setData(r.data)).catch(() => {});
    }, [content]);

    if (!data) return null;

    return (
        <section id="filosofia" className="relative bg-[#F8F9FA] text-navy" data-testid="filosofia-section" aria-labelledby="filosofia-heading">
            <div className="mx-auto max-w-[1440px] px-6 pt-24 md:px-12 md:pt-32">
                <motion.div {...reveal()} className="mx-auto mb-16 max-w-2xl text-center md:mb-20">
                    <p className="mb-5 text-[11px] uppercase tracking-[0.3em] text-gold-dim">Misión y Visión</p>
                    <h2 id="filosofia-heading" className="font-display text-4xl font-medium leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="filosofia-heading">
                        Nuestra filosofía
                    </h2>
                    <p className="mt-5 text-base text-navy/70 md:text-lg" data-testid="filosofia-subheading">Lo que nos define y hacia dónde vamos</p>
                    <span className="mx-auto mt-8 block h-px w-24 bg-gold" aria-hidden="true" />
                </motion.div>

                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
                    <Tarjeta icon={Compass} titulo="Misión" texto={data.mision} delay={0} testid="filosofia-mision-card" />
                    <Tarjeta icon={Mountain} titulo="Visión" texto={data.vision} delay={0.12} testid="filosofia-vision-card" />
                </div>
            </div>

            <div className="mt-24 bg-navy px-6 py-20 text-[#F8F9FA] md:mt-32 md:px-12 md:py-28" data-testid="filosofia-principios">
                <div className="mx-auto max-w-[1440px]">
                    <motion.h3 {...reveal()} className="mb-14 text-center font-display text-3xl font-medium tracking-tight sm:text-4xl">
                        Nuestros principios
                    </motion.h3>
                    <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                        {data.principios.map((p, i) => {
                            const Icon = PRINCIPIO_ICONS[i] || Award;
                            return (
                                <motion.div key={i} {...reveal(i * 0.08)} className="border-t border-gold/30 pt-8" data-testid={`filosofia-principio-${i}`}>
                                    <Icon size={26} strokeWidth={1.5} className="mb-6 text-gold" aria-hidden="true" />
                                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-[0.2em]">{p.nombre}</h4>
                                    <p className="text-base leading-relaxed text-[#C5CAD6]">{p.frase}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="px-6 py-24 md:px-12 md:py-32">
                <motion.figure {...reveal()} className="relative mx-auto max-w-3xl text-center" data-testid="filosofia-cita">
                    <Quote size={72} strokeWidth={1} className="mx-auto mb-6 text-gold" aria-hidden="true" />
                    <blockquote className="font-display text-2xl italic leading-snug text-navy sm:text-3xl md:text-4xl">
                        “{data.cita}”
                    </blockquote>
                    <figcaption className="mt-8 text-[11px] uppercase tracking-[0.3em] text-gold-dim">— {data.cita_autor}</figcaption>
                    <button
                        type="button"
                        onClick={() => !preview && scrollToId("servicios")}
                        className="group mt-12 inline-flex items-center gap-3 bg-navy px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F8F9FA] transition-colors duration-300 hover:bg-gold hover:text-navy"
                        data-testid="filosofia-servicios-button"
                    >
                        Conozca nuestros servicios
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </motion.figure>
            </div>
        </section>
    );
}
